import { hasDefinedValue } from '@sgme/fp';
import { DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import type { GimmickName } from 'models/forms/fields';
import { useCallback, useContext, useMemo } from 'react';

export function useGimmickSwitchInfos(
  toggleFieldIds: readonly string[],
): [
  selected: readonly GimmickName[],
  available: readonly GimmickName[],
  setter: (gimmickName: GimmickName, isSelected: boolean) => void,
] {
  const fieldMetadataById = useContext(FieldMetadataContext);
  const [dataById, { setFieldValue }] = useContext(DataContext);

  const data = useMemo(
    () =>
      toggleFieldIds.map(fieldId => {
        const metadata = fieldMetadataById[fieldId];
        if (metadata?.type !== 'GimmickSwitchField') {
          return { fieldId, gimmickName: null, isSelected: false };
        }
        const { gimmickName } = metadata;
        const isSelected = dataById[fieldId] === true;
        return { fieldId, gimmickName, isSelected };
      }),
    [toggleFieldIds, dataById, fieldMetadataById],
  );

  const setGimmickValue = useCallback(
    (gimmickName: string, value: boolean) => {
      const fieldId = data.find(i => i.gimmickName === gimmickName)?.fieldId;
      setFieldValue(fieldId!, value);
    },
    [data, setFieldValue],
  );

  const definedGimmicks = useMemo(() => data.filter(hasDefinedValue('gimmickName')), [data]);

  const available = useMemo(() => definedGimmicks.map(d => d.gimmickName), [definedGimmicks]);
  const selected = useMemo(() => definedGimmicks.filter(d => d.isSelected).map(d => d.gimmickName), [definedGimmicks]);

  return [selected, available, setGimmickValue];
}
